// Owned-territory glow (docs/ART-DIRECTION.md §1, §2.4, §2.5): a soft disc of the owner's colour under every owned
// city, between geography and the war network. Ambient, so it stays dim and on the base layer (never blooms). Sized in
// CSS px like the rest of the city art and capped by local spacing (cityArt.ts); the tuner (ROADMAP D33) retunes it in
// place. One draw call.

import { AdditiveBlending, CircleGeometry, Group, InstancedBufferAttribute, InstancedMesh, Matrix4, Quaternion, ShaderMaterial, Vector3 } from 'three'
import { InvariantViolation, NEUTRAL_OWNER, type CityIndex, type MapCityView, type MatchSetupView, type OwnerValue, type PlayerId } from '@taxwar/net'
import { GLOBE_ALTITUDE, latLonToVector3 } from '../geo/globeSpace'
import { BASE_LAYER, assignLayer } from '../layers'
import { factionRole, type Palette } from '../palette'
import { cityArtKind, type CityArt } from './cityArt'
import { TERRITORY_FRAGMENT, TERRITORY_VERTEX } from './networkShaders'

/** Between the coastline and the war network (ART-DIRECTION §2.4). */
const TERRITORY_ALTITUDE = (GLOBE_ALTITUDE.coastline + GLOBE_ALTITUDE.network) / 2
const DISC_SEGMENTS = 32
const COLOUR_COMPONENTS = 3
const Z_AXIS = new Vector3(0, 0, 1)

interface BuiltTerritory {
  readonly viewer: PlayerId
  /** In CityIndex order. */
  readonly cities: readonly MapCityView[]
  readonly owners: OwnerValue[]
  readonly mesh: InstancedMesh
  readonly colours: InstancedBufferAttribute
  readonly caps: InstancedBufferAttribute
}

export class TerritoryGlowLayer {
  readonly root = new Group()

  /** Render units per CSS px at the surface under the camera; set every frame. */
  private readonly worldPerPx = { value: 0 }
  private readonly intensity = { value: 0 }
  private readonly material = new ShaderMaterial({
    uniforms: { uIntensity: this.intensity, uWorldPerPx: this.worldPerPx },
    vertexShader: TERRITORY_VERTEX,
    fragmentShader: TERRITORY_FRAGMENT,
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
  })
  private territory: BuiltTerritory | null = null
  private palette: Palette
  private art: CityArt

  constructor(palette: Palette, art: CityArt) {
    this.palette = palette
    this.art = art
    this.intensity.value = art.brightness.glow
  }

  /** `cityArtCaps` (cityArt.ts) caps each glow by its city's local spacing. */
  setMap(view: MatchSetupView, cityArtCaps: Float32Array): void {
    this.clear()
    const count = view.cities.length
    view.cities.forEach((city, i) => {
      if (city.index !== i) throw new InvariantViolation(`setup city ${city.name} has index ${city.index}, listed at ${i}`)
    })
    const disc = new CircleGeometry(1, DISC_SEGMENTS)
    const caps = new InstancedBufferAttribute(new Float32Array(count), 1)
    disc.setAttribute('aMaxWorldPerPx', caps)
    const mesh = new InstancedMesh(disc, this.material, count)
    const colours = new InstancedBufferAttribute(new Float32Array(count * COLOUR_COMPONENTS), COLOUR_COMPONENTS)
    mesh.instanceColor = colours
    assignLayer(mesh, BASE_LAYER)
    this.root.add(mesh)
    const territory: BuiltTerritory = { viewer: view.you, cities: view.cities, owners: view.cities.map((city) => city.initialOwner), mesh, colours, caps }
    this.territory = territory
    this.writeCityArt(territory, cityArtCaps)
    for (let i = 0; i < count; i++) this.paint(territory, i)
    colours.needsUpdate = true
  }

  /** Retunes the glows (the tuner, ROADMAP D33): radius and brightness now, `cityArtCaps` from the new sizing rule. */
  setCityArt(art: CityArt, cityArtCaps: Float32Array): void {
    this.art = art
    this.intensity.value = art.brightness.glow
    if (this.territory) this.writeCityArt(this.territory, cityArtCaps)
  }

  setCityOwner(index: CityIndex, owner: OwnerValue): void {
    const territory = this.requireTerritory()
    if (index < 0 || index >= territory.owners.length) throw new InvariantViolation(`city index ${index} is not on the map`)
    if (territory.owners[index] === owner) return
    territory.owners[index] = owner
    this.paint(territory, index)
    territory.colours.needsUpdate = true
  }

  /** Repaints every glow from a new palette (a retune or a palette change). */
  setPalette(palette: Palette): void {
    this.palette = palette
    const territory = this.territory
    if (!territory) return
    for (let i = 0; i < territory.owners.length; i++) this.paint(territory, i)
    territory.colours.needsUpdate = true
  }

  clear(): void {
    const territory = this.territory
    if (!territory) return
    this.territory = null
    this.root.clear()
    territory.mesh.geometry.dispose()
    territory.mesh.dispose()
  }

  /** Once per frame, before rendering: `worldPerCssPixel` (the zoom) sizes the glows. */
  update(worldPerCssPixel: number): void {
    this.worldPerPx.value = worldPerCssPixel
  }

  dispose(): void {
    this.clear()
    this.root.removeFromParent()
    this.material.dispose()
  }

  private requireTerritory(): BuiltTerritory {
    if (!this.territory) throw new InvariantViolation('territory layer has no map')
    return this.territory
  }

  /** Each glow: a unit disc on the surface scaled to its kind's glow radius in px (the shader applies the zoom). */
  private writeCityArt(territory: BuiltTerritory, cityArtCaps: Float32Array): void {
    if (cityArtCaps.length !== territory.cities.length) throw new InvariantViolation(`${cityArtCaps.length} city art caps for ${territory.cities.length} cities`)
    const normal = new Vector3()
    const position = new Vector3()
    const rotation = new Quaternion()
    const scale = new Vector3()
    const matrix = new Matrix4()
    for (const city of territory.cities) {
      latLonToVector3(city.lat, city.lon, 1, normal)
      position.copy(normal).multiplyScalar(TERRITORY_ALTITUDE)
      rotation.setFromUnitVectors(Z_AXIS, normal)
      scale.setScalar(this.art.kinds[cityArtKind(city)].glowRadius)
      territory.mesh.setMatrixAt(city.index, matrix.compose(position, rotation, scale))
      territory.caps.setX(city.index, cityArtCaps[city.index] as number)
    }
    territory.mesh.instanceMatrix.needsUpdate = true
    territory.caps.needsUpdate = true
  }

  /** Owned cities glow in their faction colour; neutral ones are black (additive: no glow). */
  private paint(territory: BuiltTerritory, city: number): void {
    const owner = territory.owners[city] as OwnerValue
    if (owner === NEUTRAL_OWNER) {
      territory.colours.setXYZ(city, 0, 0, 0)
      return
    }
    const colour = this.palette[factionRole(owner, territory.viewer)]
    territory.colours.setXYZ(city, colour.r, colour.g, colour.b)
  }
}
